import { useState } from "react";
import { Link } from "react-router-dom";
import { GitBranch, LayoutTemplate, Users } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { HteResult } from "@/components/hte/HteResult";
import { AarPanel } from "@/components/program/AarPanel";
import { LatihanEditor } from "@/components/program/LatihanEditor";
import { MaterialViews } from "@/components/program/MaterialViews";
import { MetaEditor } from "@/components/program/MetaEditor";
import { UseTemplateButton } from "@/components/program/Templates";
import { PesertaShare } from "@/components/program/PesertaShare";
import { useMe } from "@/lib/me";
import { TrainingPlan } from "@/components/program/TrainingPlan";
import { errMsg, useApi } from "@/lib/api";
import { STATUS } from "@/lib/program";

const StatusPick = ({ kajian, onChange }) => {
  const api = useApi();
  const set = async (status) => {
    try { await api("patch", `/hte/kajian/${kajian.id}/program`, { status }); onChange({ status }); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex gap-1" data-testid="program-status-picker">
      {Object.entries(STATUS).map(([k, l]) => (
        <button key={k} onClick={() => set(k)} data-testid={`program-status-${k}`} disabled={!kajian.is_owner}
          className={`h-8 px-3 rounded-full text-xs border transition-colors ${kajian.status === k ? "bg-ink text-bg border-transparent" : "border-line text-mute hover:text-ink"}`}>{l}</button>
      ))}
    </div>
  );
};

const TemplateToggle = ({ kajian, onChange }) => {
  const api = useApi();
  const toggle = async () => {
    try { await api("patch", `/hte/kajian/${kajian.id}/program`, { templat: !kajian.templat }); onChange({ templat: !kajian.templat }); toast.success(kajian.templat ? "Dibuang daripada templat" : "Disimpan sebagai templat"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <button onClick={toggle} data-testid="program-template-toggle"
      className={`h-9 px-4 rounded-full border text-sm inline-flex items-center gap-2 transition-colors ${kajian.templat ? "border-[var(--q)] text-q bg-q-soft" : "border-line hover:bg-surface"}`}>
      <LayoutTemplate size={14} /> {kajian.templat ? "Templat ✓" : "Simpan sebagai Templat"}
    </button>
  );
};

const RasmiToggle = ({ kajian, onChange }) => {
  const api = useApi();
  const toggle = async () => {
    try { await api("patch", `/hte/kajian/${kajian.id}/program`, { rasmi: !kajian.rasmi }); onChange({ rasmi: !kajian.rasmi, templat: kajian.rasmi ? kajian.templat : true }); toast.success(kajian.rasmi ? "Dibuang daripada Templat Rasmi" : "Diterbitkan sebagai Templat Rasmi ATAS"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <button onClick={toggle} data-testid="program-rasmi-toggle"
      className={`h-9 px-4 rounded-full border text-sm inline-flex items-center gap-2 transition-colors ${kajian.rasmi ? "border-[var(--q)] text-q bg-q-soft" : "border-line hover:bg-surface"}`}>
      {kajian.rasmi ? "Templat Rasmi ✓" : "Terbitkan sebagai Templat Rasmi"}
    </button>
  );
};

export const ProgramResult = ({ kajian, setKajian }) => {
  const me = useMe();
  const [tab, setTab] = useState(window.location.hash === "#aar" ? "aar" : "kerangka");
  const p = kajian.program;
  const patch = (x) => setKajian({ ...kajian, ...x });
  const owner = kajian.is_owner !== false;
  return (
    <div className="space-y-6" data-testid="program-result">
      <header className="space-y-3">
        <div className="eyebrow">Program · {kajian.rujukan}</div>
        <h1 className="font-display text-3xl sm:text-4xl text-ink" data-testid="program-title">{p.tajuk}</h1>
        <p className="text-sm text-mute" data-testid="program-meta-line">{p.peserta} · {p.tempoh}</p>
        {kajian.dari_templat && <p className="text-xs text-mute" data-testid="program-from-template">Daripada {kajian.dari_templat.rasmi ? "Templat Rasmi ATAS" : "templat"}: {kajian.dari_templat.rasmi ? <span className="text-ink">{kajian.dari_templat.tajuk}</span> : <Link to={`/kajian/${kajian.dari_templat.id}`} className="underline underline-offset-4">{kajian.dari_templat.tajuk}</Link>}</p>}
        <div className="flex flex-wrap items-center gap-3">
          <StatusPick kajian={kajian} onChange={patch} />
          {owner && <>
            <MetaEditor kajian={kajian} onChange={patch} />
            <TemplateToggle kajian={kajian} onChange={(x) => patch(x.templat === false ? { ...x, rasmi: false } : x)} />
            {me?.role === "superadmin" && <RasmiToggle kajian={kajian} onChange={patch} />}
            <PesertaShare kajian={kajian} onChange={patch} />
            {kajian.templat && <UseTemplateButton id={kajian.id} testid="program-use-template" />}
            <Link to={`/program?dari=${kajian.id}`} data-testid="program-next-version-link" className="h-9 px-4 rounded-full border border-line text-sm inline-flex items-center gap-2 hover:bg-surface"><GitBranch size={14} /> Bina Versi Seterusnya</Link>
            <Link to={`/program?dari=${kajian.id}&jenis=peserta`} data-testid="program-adapt-link" className="h-9 px-4 rounded-full border border-line text-sm inline-flex items-center gap-2 hover:bg-surface"><Users size={14} /> Sesuaikan Peserta</Link>
          </>}
        </div>
      </header>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="kerangka" data-testid="program-tab-kerangka">Sebelum · Kerangka</TabsTrigger>
          <TabsTrigger value="bahan" data-testid="program-tab-bahan">Semasa · Bahan</TabsTrigger>
          <TabsTrigger value="aar" data-testid="program-tab-aar">Selepas · AAR ({(kajian.aar || []).length}){(kajian.maklum_balas || []).length ? ` · ${kajian.maklum_balas.length} maklum balas` : ""}</TabsTrigger>
          {owner && <TabsTrigger value="sunting" data-testid="program-tab-sunting">Sunting</TabsTrigger>}
          <TabsTrigger value="hte" data-testid="program-tab-hte">Analisis HTE</TabsTrigger>
        </TabsList>
        <TabsContent value="kerangka" className="pt-6"><TrainingPlan kajian={kajian} /></TabsContent>
        <TabsContent value="bahan" className="pt-6"><MaterialViews kajian={kajian} /></TabsContent>
        <TabsContent value="aar" className="pt-6">{owner ? <AarPanel kajian={kajian} onChange={patch} /> : <p className="text-sm text-mute">Hanya pemilik boleh mencatat AAR.</p>}</TabsContent>
        {owner && <TabsContent value="sunting" className="pt-6"><LatihanEditor key={kajian.version_count} kajian={kajian} onSaved={(k) => { patch(k); setTab("kerangka"); }} /></TabsContent>}
        <TabsContent value="hte" className="pt-6"><HteResult kajian={kajian} onUpdated={(k) => patch(k)} /></TabsContent>
      </Tabs>
    </div>
  );
};
